import React, { useEffect, useState } from "react";
import Modal from "react-modal";
import "../css/DynamicFormModal.css";
import { useAlert } from '../../contexts/ToastContext';

Modal.setAppElement("#root");

export default function DynamicFormModal({
  /** true → modal visible */
  isOpen,
  /** called when the user clicks the cancel/close button */
  onRequestClose,
  /** full description of the dialog – see saveModalFactory.js */
  config,
}) {
  const {
    title,
    fields,
    onSubmit,
    className = "",
    style,
    secondaryButton,
  } = config;

  // ----------- form state ------------------------------------
  const [values, setValues] = useState({});
  const [errors, setErrors] = useState({});
  const [optionsCache, setOptionsCache] = useState({});
  const [loadingOpts, setLoadingOpts] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const showAlert = useAlert();

  // initialise empty values each time the modal opens
  useEffect(() => {
    if (isOpen) {
      const init = {};
      fields.forEach((f) => {
        // Always use defaultValue if provided, otherwise empty string
        init[f.name] = f.defaultValue || "";
      });
      setValues(init);
      setErrors({});
      setOptionsCache({});
    }
  }, [isOpen, fields]);

  // helper to update a single field
  const setValue = (name, val) => {
    setValues((p) => ({ ...p, [name]: val }));
    // Clear error for this field when value changes
    if (errors[name]) {
      setErrors((p) => ({ ...p, [name]: "" }));
    }
  };

  // ---------- async option loaders (for selects) ----------
  useEffect(() => {
    if (!isOpen) return;

    const loadOptions = async () => {
      const cache = {};
      setLoadingOpts(true);
      
      for (const f of fields) {
        if (f.type === "select" && f.loadOptions) {
          try {
            const opts = await f.loadOptions(values);
            cache[f.name] = opts || [];
          } catch (e) {
            console.error(`⚠️ loadOptions failed for ${f.name}`, e);
            cache[f.name] = [];
          }
        }
      }
      
      setOptionsCache(cache);
      setLoadingOpts(false);
    };

    loadOptions();
  }, [isOpen, fields, values]);

  // ---------- validation ------------------------------------
  const validateAll = () => {
    const newErr = {};

    fields.forEach((f) => {
      const v = values[f.name];
      
      // Skip validation for custom components that don't have required flag
      if (f.type === 'custom' && !f.required) return;
      
      if (f.required && (!v || v === "" || (Array.isArray(v) && v.length === 0))) {
        newErr[f.name] = "Required";
        return;
      }
      
      if (f.type === "select" && f.required && (!v || v === "")) {
        newErr[f.name] = "Please choose an option";
        return;
      }
      
      if (f.type === "text" && f.validate) {
        const msg = f.validate(v);
        if (msg) newErr[f.name] = msg;
      }
    });

    setErrors(newErr);
    return Object.keys(newErr).length === 0;
  };

  // ---------- submit ----------------------------------------
  const handlePrimary = async () => {
    if (!validateAll()) return;
    
    setSubmitting(true);
    try {
      await onSubmit(values, onRequestClose);
    } catch (e) {
      console.error("Submit error:", e);
      showAlert(e?.message ?? "Something went wrong");
    } finally {
      setSubmitting(false);
    }
  };

  // ---------- handle modal close ----------------------------
  const handleClose = () => {
    setValues({});
    setErrors({});
    setSubmitting(false);
    onRequestClose();
  };

  // ---------- render a single field -------------------------
  const renderField = (field) => {
    const common = {
      id: field.name,
      name: field.name,
      value: values[field.name] ?? "",
      onChange: (e) => setValue(field.name, e.target.value),
      disabled: submitting,
    };

    switch (field.type) {
      case "text":
      case "password":
      case "email":
      case "number":
        return (
          <input
            type={field.type}
            placeholder={field.placeholder}
            {...common}
            className="dfm-input"
          />
        );

      case "select":
        const opts = field.options ?? optionsCache[field.name] ?? [];
        const isDisabled = field.disabled || loadingOpts || submitting;
        
        return (
          <select
            {...common}
            className="dfm-select"
            disabled={isDisabled}
          >
            <option value="">— Select —</option>
            {opts.map((o) => (
              <option key={o.value} value={o.value}>
                {o.label}
              </option>
            ))}
          </select>
        );

      case "custom":
        if (field.component) {
          const Component = field.component;
          return (
            <Component 
              values={values} 
              setValue={setValue}
              errors={errors}
              field={field}
            />
          );
        }
        if (field.render) {
          return field.render({
            values,
            setValue,
            errors,
            field,
          });
        }
        return <div>Custom field configuration error</div>;

      default:
        return <div>Unsupported field type: {field.type}</div>;
    }
  };

  // ---------- JSX -------------------------------------------
  return (
    <Modal
      isOpen={isOpen}
      onRequestClose={handleClose}
      className={["dfm-modal", className].filter(Boolean).join(" ")}
      overlayClassName="dfm-overlay"
      style={style ? { content: style } : undefined}
      shouldCloseOnOverlayClick={!submitting}
      shouldCloseOnEsc={!submitting}
    >
      <div className="dfm-header">
        <h3 className="dfm-title">{title}</h3>
        <button 
          className="dfm-close" 
          onClick={handleClose}
          disabled={submitting}
        >
          ×
        </button>
      </div>

      <div className="dfm-body">
        {fields.map((fld) => (
          <div key={fld.name} className="dfm-field">
            {fld.label && (
              <label htmlFor={fld.name} className="dfm-label">
                {fld.label}
                {fld.required && <span className="dfm-required">*</span>}
              </label>
            )}

            {renderField(fld)}

            {fld.hint && (
              <small className="dfm-hint">{fld.hint}</small>
            )}
            {errors[fld.name] && (
              <small className="dfm-error">{errors[fld.name]}</small>
            )}
          </div>
        ))}
      </div>

      <div className="dfm-footer">
        {secondaryButton ? (
          <button
            className="dfm-btn dfm-btn-secondary"
            onClick={secondaryButton.onClick || handleClose}
            disabled={submitting}
          >
            {secondaryButton.label}
          </button>
        ) : (
          <button
            className="dfm-btn dfm-btn-secondary"
            onClick={handleClose}
            disabled={submitting}
          >
            Cancel
          </button>
        )}

        <button
          className="dfm-btn dfm-btn-primary"
          onClick={handlePrimary}
          disabled={submitting || loadingOpts}
        >
          {submitting ? "Saving…" : "Save"}
        </button>
      </div>
    </Modal>
  );
}